import { useCallback, useState } from 'react';
import { I18n } from 'react-redux-i18n';

import { FolderListDto } from '@common/dto/folder/list-dto';
import { FolderGetDto } from '@common/dto/folder/get-dto';
import { HttpErrorDto } from '@common/dto/http';
import { getFolderSecuritySettingsClasses as  getFolderSecuritySettingsClassesService } from "@common/actions/user-maintenance/classes";
import { FolderManagementActionMode } from '@common/components/folder-management';
import { executeUI } from '@common/utils/ui-bridge';
import { deletePedigreeFolder, getFolders, getPedigreeFolder, movePedigreeFolder, renamePedigreeFolder, savePedigreeFolder as savePedigreeFolder } from "@common/actions/folders";

import { MessagePopupState } from './controller.types';
import { spreadSheetsModuleName } from '../constants/shared';

export type FolderManagementDialogProps = {
  opened: boolean;
  mode?: FolderManagementActionMode;
  folder?: FolderGetDto;
};

export type MoveFolderDialogProps = {
  opened: boolean;
  folder?: FolderGetDto;
};

export type ControllerProps = {
  showErrorMessage: (message?: string, size?: MessagePopupState['size']) => void;
};

export const useFolderController = ({ showErrorMessage }: ControllerProps) => {
  const [loadingFolders, setLoadingFolders] = useState(false);
  const [folders, setFolders] = useState<Array<FolderListDto>>([]);
  const [folderManagementDialogProps, setFolderManagementDialogProps] = useState<FolderManagementDialogProps>({ opened: false });
  const [moveFolderDialogProps, setMoveFolderDialogProps] = useState<MoveFolderDialogProps>({ opened: false });

  const getPedigreesModuleFolders = useCallback(async () => {
    const pedigreesContext = 2;
    const res = await getFolders({ context: pedigreesContext });

    return res;
  }, []);

  const loadAndSetFolders = useCallback(async () => {
    try {
      setLoadingFolders(true);
      const res = await getPedigreesModuleFolders();
      setFolders(res);

      return res;
    } finally {
      setLoadingFolders(false);
    }
  }, [showErrorMessage, getPedigreesModuleFolders]);

  const getFolderSecuritySettingsClasses = useCallback( async () => {
    try {
      const folderSecuritySettings = await getFolderSecuritySettingsClassesService();
      
      return folderSecuritySettings;
    } catch (err) {
      console.error(err);

      const errorMessage = (err as HttpErrorDto)?.entity || I18n.t('Failed to get Folder Security Settings');
      showErrorMessage(errorMessage);
    }
  }, [showErrorMessage]);

  const getFolder = useCallback(async (folderId: FolderGetDto['id']) => {
    try {
      const folder = await getPedigreeFolder(folderId);
      
      return folder;
    } catch (err) { // TODO move error handling to the shared component
      console.error(err);

      const errorMessage = (err as HttpErrorDto)?.entity || I18n.t('Failed to get selected folder');
      showErrorMessage(errorMessage);
    }
  }, [showErrorMessage]);

  const toggleMoveFolderDialog = useCallback(({ opened, folder }: MoveFolderDialogProps) => {
    setMoveFolderDialogProps((prev) => ({
      ...prev,
      opened,
      folder
    }));
  }, [setMoveFolderDialogProps]);

  const openMoveFolderDialog = useCallback((folderId: FolderGetDto["id"], folder: FolderGetDto) => {
    toggleMoveFolderDialog({ opened: true, folder });
  }, [toggleMoveFolderDialog]);

  const openPedigreeSpreadsheetForFolder = useCallback((folderId: FolderGetDto["id"], folder: FolderGetDto) => {
    executeUI(spreadSheetsModuleName, 'openPSSFolder', null, null, folderId);
  }, []);

  const openIndividualSpreadsheetForFolder = useCallback((folderId: FolderGetDto["id"], folder: FolderGetDto) => {
    executeUI(spreadSheetsModuleName, 'openISS', null, null, folderId);
  }, []);

  const closeMoveFolderDialog = useCallback(() => {
    toggleMoveFolderDialog({ opened: false });
  }, [toggleMoveFolderDialog]);

  const moveFolder = useCallback(async (folderDto: { folderId: FolderGetDto['id'], newFolderId: FolderGetDto['id'] }) => {
    try {
      await movePedigreeFolder(folderDto);
      closeMoveFolderDialog();
      loadAndSetFolders();
    } catch (err) {
      console.error(err);

      const errorMessage = (err as HttpErrorDto)?.entity || I18n.t("Failed to delete folder");
      showErrorMessage(errorMessage);
    }
  }, [showErrorMessage, loadAndSetFolders, closeMoveFolderDialog]);

  const renameFolder = useCallback(async (folderId: FolderListDto['id'], name: string) => {
    try {
      await renamePedigreeFolder({ id: folderId, name });
      loadAndSetFolders();
    } catch (err) {
      console.error(err);

      const errorMessage = (err as HttpErrorDto)?.entity || I18n.t('Failed to rename folder');
      showErrorMessage(errorMessage);
    }
  }, [loadAndSetFolders, showErrorMessage]);

  const getDefaultFolderId = useCallback((list = folders): FolderListDto["id"] | undefined => {
    const firstFolder = list[0];

    return firstFolder?.id;
  }, [folders]);
  
  const toggleFolderManagementDialog = useCallback(
    ({ opened, folder, mode }: FolderManagementDialogProps) => {
      setFolderManagementDialogProps((prev) => ({
        ...prev,
        opened,
        folder,
        mode,
      }));
    },
    [setFolderManagementDialogProps]
  );

  const openNewFolderOpen = useCallback(
    (folderId?: FolderGetDto["id"], folder?: FolderGetDto) => {
      toggleFolderManagementDialog({
        opened: true,
        folder,
        mode: FolderManagementActionMode.create,
      });
    },
    [toggleFolderManagementDialog]
  );

  const closeFolderManagementDialog = useCallback(() => {
    toggleFolderManagementDialog({ opened: false });
  }, [toggleFolderManagementDialog]);

  const openEditFolderDialog = useCallback((folderId: FolderGetDto["id"], folder: FolderGetDto) => {
    toggleFolderManagementDialog({ opened: true, folder, mode: FolderManagementActionMode.edit });
  }, [toggleFolderManagementDialog]);

  return {
    getPedigreesModuleFolders,
    loadAndSetFolders,
    folders,
    loadingFolders,
    folderManagementDialogProps,
    getFolder,
    getDefaultFolderId,
    getFolderSecuritySettingsClasses,
    openEditFolderDialog,
    closeFolderManagementDialog,
    toggleFolderManagementDialog,
    openNewFolderOpen,
    saveFolder: savePedigreeFolder,
    deleteFolder: deletePedigreeFolder,
    renameFolder,
    moveFolder,
    moveFolderDialogProps,
    openMoveFolderDialog,
    closeMoveFolderDialog,
    openPedigreeSpreadsheetForFolder,
    openIndividualSpreadsheetForFolder
  };
};